'use client';

import React, { useMemo } from 'react';
import chrome from '@/shared/layouts/components/navigation/AppChrome.module.css';

export type SystemStatusEnv = 'ok' | 'warn' | 'maintenance' | 'down';

interface SystemStatusIndicatorProps {
  className?: string;
  /** Optional override for tests / storybook; defaults to NEXT_PUBLIC_STATUS. */
  status?: SystemStatusEnv | string;
}

/**
 * Navbar system status chip — same label language and pill styles as Footer
 * (All systems / Degraded / Maintenance / Outage). Text + status dot, not color alone.
 * Links to the **public status subdomain** (not portal /status).
 * DEV: NEXT_PUBLIC_STATUS_URL or https://status.sandbox.mechagora.net
 */
function statusPageHref(): string {
  const fromEnv = process.env.NEXT_PUBLIC_STATUS_URL?.trim()
  if (fromEnv) return fromEnv
  // Default DEV sandbox host; prod should set NEXT_PUBLIC_STATUS_URL=https://status.mechagora.com
  return 'https://status.sandbox.mechagora.net'
}

export function SystemStatusIndicator({
  className,
  status: statusProp,
}: SystemStatusIndicatorProps) {
  const statusEnv = (
    statusProp ??
    process.env.NEXT_PUBLIC_STATUS ??
    'ok'
  ).toLowerCase();

  const { statusClass, label, dataStatus } = useMemo(() => {
    if (statusEnv === 'down') {
      return {
        statusClass: chrome.statusDown,
        label: 'Outage',
        dataStatus: 'down' as const,
      };
    }
    if (statusEnv === 'warn') {
      return {
        statusClass: chrome.statusWarn,
        label: 'Degraded',
        dataStatus: 'warn' as const,
      };
    }
    if (statusEnv === 'maintenance') {
      return {
        statusClass: chrome.statusWarn,
        label: 'Maintenance',
        dataStatus: 'maintenance' as const,
      };
    }
    return {
      statusClass: chrome.statusOk,
      label: 'All systems',
      dataStatus: 'ok' as const,
    };
  }, [statusEnv]);

  const announced = `System status: ${label}`;
  const href = statusPageHref()

  return (
    <a
      href={href}
      className={`${chrome.statusPill} ${statusClass}${className ? ` ${className}` : ''}`}
      aria-label={announced}
      title={announced}
      data-status={dataStatus}
      // Slightly denser than footer for navbar height
      style={{ minHeight: 32, padding: '0 12px' }}
    >
      <span className={chrome.statusDot} aria-hidden="true" />
      <span
        className={chrome.statusText}
        aria-live="polite"
        aria-atomic="true"
      >
        {label}
      </span>
    </a>
  );
}

export default SystemStatusIndicator;
